import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, API_BASE } from '@/lib/api';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  BookOpen, Layers, Inbox, Home, Bell, Compass, Mail, FileText, Users, ShieldAlert,
  CalendarDays, Target, Clock, Database, BarChart3, TrendingUp, Wallet, ListChecks,
  Settings, Wrench, ClipboardList, Plane, ReceiptText, Plus, FileDown, History, GraduationCap,
  FileSearch, MessageSquare,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { canSeeAudit, canSeeWOs, canSeeQuotes, canSeeVisits, canSeePendingInvoices, isOwner } from '@/lib/roles';
import { useFeature } from '@/lib/features';
import { getRecentPages } from '@/lib/recent';
import { toast } from 'sonner';

const REPORT_LINKS = [
  { tab: 'project_health',    label: 'Project Health',    Icon: TrendingUp, roles: ['owner', 'dm_creative', 'dm_technical', 'dm_architecture', 'operations'] },
  { tab: 'financial_summary', label: 'Financial Summary', Icon: Wallet,     roles: ['owner', 'dm_creative', 'dm_technical', 'dm_architecture', 'accounts'] },
  { tab: 'team_workload',     label: 'Team Workload',     Icon: Users,      roles: ['owner', 'dm_creative', 'dm_technical', 'dm_architecture'] },
  { tab: 'milestone_tracker', label: 'Milestone Tracker', Icon: ListChecks, roles: ['owner', 'dm_creative', 'dm_technical', 'dm_architecture', 'operations'] },
];

export const CommandPalette = ({ open, onOpenChange }) => {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [res, setRes] = useState({ pages: [], projects: [], work_orders: [] });

  const fKra = useFeature('kra');
  const fTimesheets = useFeature('timesheets');
  const fRhythmV4 = useFeature('operating_rhythm_v4');
  const fProjects = useFeature('projects');
  const fWorkOrders = useFeature('work_orders');
  const fQuotes = useFeature('quotes');
  const fVisits = useFeature('visits');
  const fKnowledge = useFeature('knowledge_hub');
  const fTemplates = useFeature('templates');
  const fSettings = useFeature('settings');
  const fReports = useFeature('reports');
  const fTeamDirectory = useFeature('team_directory');
  const fLearningHub = useFeature('learning_hub');
  const fSopSearch = useFeature('sop_search');

  useEffect(() => {
    let alive = true;
    if (!q) { setRes({ pages: [], projects: [], work_orders: [], sops: [] }); return; }
    const id = setTimeout(async () => {
      try {
        const r = await api.get('/search', { params: { q } });
        if (alive) setRes(r.data);
      } catch (e) { console.debug('palette search skipped:', e?.response?.status || e); }
    }, 180);
    return () => { alive = false; clearTimeout(id); };
  }, [q]);

  const go = (path) => { onOpenChange(false); setQ(''); nav(path); };
  const role = user?.role;
  const canEditMaster = ['owner', 'dm_creative', 'dm_technical', 'dm_architecture'].includes(role);
  const recent = open ? getRecentPages().filter(r => r.path !== location.pathname) : [];

  const downloadTemplate = async () => {
    onOpenChange(false);
    try {
      const token = localStorage.getItem('32d.os.token');
      const r = await fetch(`${API_BASE}/master-projects/import/template.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'master-projects-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded.');
    } catch { toast.error('Could not download the template.'); }
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} data-testid="command-palette-dialog">
      <CommandInput placeholder="Search pages, projects, work orders… or jump anywhere"
                    value={q} onValueChange={setQ} data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>

        {(fRhythmV4 || canEditMaster) && (
          <CommandGroup heading="Quick actions">
            {fRhythmV4 && (
              <CommandItem onSelect={() => go('/my-week?new=1')} data-testid="palette-action-new-commitment">
                <Plus size={14} className="mr-2" />New commitment
              </CommandItem>
            )}
            {fRhythmV4 && (
              <CommandItem onSelect={() => go('/my-week')} data-testid="palette-action-my-milestones">
                <ListChecks size={14} className="mr-2" />My milestones (My Week)
              </CommandItem>
            )}
            {canEditMaster && fProjects && (
              <CommandItem onSelect={downloadTemplate} data-testid="palette-action-download-template">
                <FileDown size={14} className="mr-2" />Download master project template
              </CommandItem>
            )}
          </CommandGroup>
        )}

        {recent.length > 0 && (
          <CommandGroup heading="Recent">
            {recent.map(r => (
              <CommandItem key={r.path} onSelect={() => go(r.path)} data-testid="palette-recent-item">
                <History size={14} className="mr-2" />{r.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Workspace">
          <CommandItem data-testid="command-palette-result-item" onSelect={() => go('/home')}><Home size={14} className="mr-2" />Home</CommandItem>
          {fRhythmV4 && <CommandItem onSelect={() => go('/my-week')} data-testid="palette-nav-my-week"><CalendarDays size={14} className="mr-2" />My Week</CommandItem>}
          {fRhythmV4 && <CommandItem onSelect={() => go('/commitments')} data-testid="palette-nav-commitments"><Target size={14} className="mr-2" />Commitments</CommandItem>}
          {fKra && <CommandItem onSelect={() => go('/kra')}><Target size={14} className="mr-2" />My KRAs</CommandItem>}
          {fTimesheets && <CommandItem onSelect={() => go('/timesheets')}><Clock size={14} className="mr-2" />Timesheets</CommandItem>}
          {fProjects && <CommandItem onSelect={() => go('/projects')}><Layers size={14} className="mr-2" />Projects</CommandItem>}
          {fProjects && <CommandItem onSelect={() => go('/master-projects')} data-testid="palette-nav-master-projects"><Database size={14} className="mr-2" />Master Project Data</CommandItem>}
          {fTeamDirectory && <CommandItem onSelect={() => go('/team')} data-testid="palette-nav-team"><Users size={14} className="mr-2" />Team Directory</CommandItem>}
          {fLearningHub && <CommandItem onSelect={() => go('/guide')} data-testid="palette-nav-guide"><GraduationCap size={14} className="mr-2" />How to use</CommandItem>}
          {fWorkOrders && canSeeWOs(role) && <CommandItem onSelect={() => go('/work-orders')}><Inbox size={14} className="mr-2" />Work Orders</CommandItem>}
          {fQuotes && canSeeQuotes(role) && <CommandItem onSelect={() => go('/quotes')}><ClipboardList size={14} className="mr-2" />Quote Register</CommandItem>}
          {fVisits && canSeeVisits(role) && <CommandItem onSelect={() => go('/visits')}><Plane size={14} className="mr-2" />Travel & Visits</CommandItem>}
          {canSeePendingInvoices(role) && <CommandItem onSelect={() => go('/pending-invoices')}><ReceiptText size={14} className="mr-2" />Pending Invoices</CommandItem>}
          <CommandItem onSelect={() => go('/notifications')}><Bell size={14} className="mr-2" />Notifications</CommandItem>
          {fSettings && <CommandItem onSelect={() => go('/settings')}><Settings size={14} className="mr-2" />Settings</CommandItem>}
        </CommandGroup>

        {fReports && (
          <CommandGroup heading="Reports">
            <CommandItem onSelect={() => go('/reports')} data-testid="palette-nav-reports"><BarChart3 size={14} className="mr-2" />Reports</CommandItem>
            {REPORT_LINKS.filter(r => r.roles.includes(role)).map(r => (
              <CommandItem key={r.tab} onSelect={() => go(`/reports?tab=${r.tab}`)} data-testid={`palette-report-${r.tab}`}>
                <r.Icon size={14} className="mr-2" />Report: {r.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Knowledge">
          {fKnowledge && <CommandItem onSelect={() => go('/knowledge')}><BookOpen size={14} className="mr-2" />Knowledge Hub</CommandItem>}
          {fTemplates && <CommandItem onSelect={() => go('/templates')}><FileText size={14} className="mr-2" />Templates</CommandItem>}
          {fTemplates && <CommandItem onSelect={() => go('/emails')}><Mail size={14} className="mr-2" />Email Templates</CommandItem>}
          <CommandItem onSelect={() => go('/wizard')}><Compass size={14} className="mr-2" />What next? Wizard</CommandItem>
        </CommandGroup>

        {(isOwner(role) || canSeeAudit(role)) && (
          <CommandGroup heading="Admin">
            {isOwner(role) && <CommandItem onSelect={() => go('/admin/users')}><Users size={14} className="mr-2" />Admin: Users</CommandItem>}
            {isOwner(role) && <CommandItem onSelect={() => go('/admin/settings')}><Wrench size={14} className="mr-2" />Admin: Settings</CommandItem>}
            {isOwner(role) && <CommandItem onSelect={() => go('/admin/holidays')}><CalendarDays size={14} className="mr-2" />Admin: Holiday Calendar</CommandItem>}
            {isOwner(role) && <CommandItem onSelect={() => go('/admin/feedback')} data-testid="palette-nav-admin-feedback"><MessageSquare size={14} className="mr-2" />Admin: Team Feedback</CommandItem>}
            {canSeeAudit(role) && <CommandItem onSelect={() => go('/admin/audit')}><ShieldAlert size={14} className="mr-2" />Admin: Audit Log</CommandItem>}
          </CommandGroup>
        )}

        {fSopSearch && res.sops?.length > 0 && (
          <CommandGroup heading="SOPs">
            {res.sops.map(s => (
              <CommandItem key={s.id}
                           value={`sop ${s.sop_code || ''} ${s.title || ''} ${s.sop_category || ''} ${s.sop_owner || ''} ${q}`}
                           onSelect={() => go(`/knowledge/${s.id}`)}
                           data-testid={`palette-sop-${s.sop_code || s.id}`}>
                <FileSearch size={14} className="mr-2 text-[hsl(var(--primary))]" />
                <span className="flex-1">{s.title}</span>
                {s.sop_code && <span className="ml-2 text-[11px] text-[hsl(var(--foreground-subtle))] font-mono">{s.sop_code}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {res.pages?.length > 0 && (
          <CommandGroup heading="Knowledge pages">
            {res.pages.map(p => (
              <CommandItem key={p.id}
                           value={`page ${p.title || ''} ${q}`}
                           onSelect={() => go(`/knowledge/${p.id}`)}>
                <BookOpen size={14} className="mr-2" />{p.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.projects?.length > 0 && (
          <CommandGroup heading="Projects">
            {res.projects.map(p => (
              <CommandItem key={p.id}
                           value={`project ${p.name || ''} ${p.code || ''} ${p.client_name || ''} ${q}`}
                           onSelect={() => go(`/projects/${p.id}`)}>
                <Layers size={14} className="mr-2" />{p.name}
                <span className="ml-auto text-[11px] text-[hsl(var(--foreground-subtle))] font-mono">{p.code}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.work_orders?.length > 0 && (
          <CommandGroup heading="Work Orders">
            {res.work_orders.map(w => (
              <CommandItem key={w.id}
                           value={`wo ${w.client_name || ''} ${w.wo_number || ''} ${w.original_filename || ''} ${q}`}
                           onSelect={() => go(`/work-orders/${w.id}/verify`)}>
                <Inbox size={14} className="mr-2" />{w.client_name || w.original_filename}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
